import { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Download, Trash2, FileText, Paperclip } from 'lucide-react';

const MAX_BYTES = 10 * 1024 * 1024; // keep in sync with the backend limit

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const CaseAttachments = ({ caseId }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/cases/${caseId}/attachments`);
      setAttachments(res.data);
    } catch (err) {
      toast.error('Failed to load attachments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error('File exceeds the 10MB limit');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readAsDataUrl(file);
      await api.post(
        `/cases/${caseId}/attachments`,
        {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          data: dataUrl,
        }
      );
      toast.success('Attachment uploaded');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (att) => {
    try {
      const res = await api.get(
        `/cases/${caseId}/attachments/${att.id}/download`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete ${att.filename}?`)) return;
    try {
      await api.delete(
        `/cases/${caseId}/attachments/${att.id}`
      );
      toast.success('Attachment deleted');
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="case-attachments">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">Documents and files linked to this case</p>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            data-testid="attachment-file-input"
            onChange={handleFile}
          />
          <Button
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
            data-testid="attachment-upload-button"
          >
            <Upload size={16} className="mr-2" />
            {uploading ? 'Uploading…' : 'Upload File'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading attachments…</p>
      ) : attachments.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Paperclip className="mx-auto text-muted-foreground mb-3" size={40} />
            <p className="text-muted-foreground">No attachments yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {attachments.map((att) => (
            <Card key={att.id} data-testid={`attachment-${att.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <FileText className="text-muted-foreground shrink-0" size={24} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{att.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatSize(att.size)}
                    {att.uploaded_at
                      ? ` · ${new Date(att.uploaded_at).toLocaleDateString()}`
                      : ''}
                    {att.description ? ` · ${att.description}` : ''}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(att)}
                  aria-label={`Download ${att.filename}`}
                  data-testid={`attachment-download-${att.id}`}
                >
                  <Download size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(att)}
                  aria-label={`Delete ${att.filename}`}
                  data-testid={`attachment-delete-${att.id}`}
                >
                  <Trash2 size={16} className="text-destructive" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseAttachments;
